// texq.js — TEXTURE QUALITY: the ONE switch (tex-quality, 2026-11-02; APP_TEXQUALITY.md).
//
// TEX_HQ is the only switch. ON (the owner's decision 2026-11-02: sharper textures on EVERY device
// class) every reader below takes its class's row of TEX_Q; OFF every reader returns exactly the value
// its caller passes in (the pre-switch setup, byte for byte: the same file, the same sampler state).
// Flipping it changes nothing else — no shader text, no program key, no budget, no bake.
//
// What it governs (colour textures only; EXACT-DATA textures — class / res / ways maps, masks, the
// pngexact / maskfill PNGs, index and lookup textures — never pass here and are never filtered):
//   facade  the facade atlas: `meta.buildings.facades.file_hq` (UASTC, pipeline/atlasktx2.mjs
//           ATLAS_KTX2_HQ → ASTC 4x4 on the Quest / phones, BC7 on desktop) instead of `file`
//           (ETC1S → ETC2 RGB / BC7), + its anisotropy (buildings.js)
//   ground  the GROUND-M pool's anisotropy = the sampler's AND uVtPick.y's level pick (gmtex.js GmTex)
//   mat     the ground material arrays' anisotropy (groundmat.js stackOf)
// A bake without `file_hq` (built before the twin) keeps `file` under either state.
//
// The rows are measured (APP_TEXQUALITY.md: GPU ms per anisotropy on the probe, bytes,
// transcode ms). A class absent from TEX_Q, or no class decided yet (deviceClass() null), keeps the
// caller's value. Move to device.js CLASS_* later if the class tables are merged there.
import { deviceClass } from './device.js';

export const TEX_HQ = true;

// ground stays 16 on every class: it is also uVtPick.y's divisor (max(minor, major / aniso)), so a lower value
// picks a COARSER pool level at grazing angles (blurrier ground, the opposite of the aim); the probe arms
// (Intel MTL iGPU, quest + desktop tiers, aniso 4 / 8 / 16 on every kind) showed no GPU-ms effect above the
// arm-to-arm noise (±20 %), so the desktop rows take 16 and the mobile GPUs (unmeasured Adreno / Mali; the
// sampler cost is bandwidth) take 8 on the facade and the material arrays, phone-lo keeps today's 4 there.
export const TEX_Q = {
  'desktop-dgpu': { facade_hq: true, facade: 16, ground: 16, mat: 16 },
  'desktop-igpu': { facade_hq: true, facade: 16, ground: 16, mat: 16 },
  'phone-hi': { facade_hq: true, facade: 8, ground: 16, mat: 8 },
  'phone-lo': { facade_hq: true, facade: 8, ground: 16, mat: 4 },
  quest: { facade_hq: true, facade: 8, ground: 16, mat: 8 },
};

function row(cls) { return TEX_HQ ? TEX_Q[cls] || null : null; }

// the anisotropy of `kind` for the class: its row's value under TEX_HQ, else `off` (the caller's own)
export function texAniso(kind, off, cls = deviceClass()) {
  const r = row(cls);
  return r && r[kind] != null ? r[kind] : off;
}

// the facade atlas file of a meta.buildings.facades block: file_hq under TEX_HQ when the class takes it
// and the bake ships it, else file
export function facadeFile(facades, cls = deviceClass()) {
  const r = row(cls);
  return r && r.facade_hq && facades.file_hq ? facades.file_hq : facades.file;
}

// the state for the load report / a probe: {hq, cls, row}
export function texqState(cls = deviceClass()) { return { hq: TEX_HQ, cls, row: row(cls) }; }
